/**
 * v0.5.0 Settings 「长期记忆」 section.
 *
 * Lists the daemon's long-term memories (active + pending), lets the user
 * confirm a pending entry ("确认保存"), delete one, or clear all. Memory text is
 * displayed directly but treated as untrusted data; every mutation replaces the
 * list with the daemon's response — no optimistic/fake success.
 *
 * Transport: demo mode uses the mock client; live mode has no memory relay op
 * yet, so the section shows an explicit unavailable reason and no rows.
 */
import { useEffect, useState } from 'react';
import { formatRelative } from '../lib/format';
import { nowSeconds } from '../lib/id';
import {
  memoryCounts,
  memoryKindLabel,
  memoryNeedsApproval,
  memoryStatusLabel,
  partitionMemories,
  type MemoryEntry,
} from '../lib/memory';
import { useApp } from '../state/AppState';
import { ConfirmDialog } from './ConfirmDialog';
import { Icon } from './Icon';

function KindBadge({ entry }: { entry: MemoryEntry }) {
  return (
    <span className="rounded-full border border-indigo-400/30 bg-indigo-400/10 px-1.5 py-0.5 text-[9px] text-indigo-200">
      {memoryKindLabel(entry.kind)}
    </span>
  );
}

function StatusBadge({ entry }: { entry: MemoryEntry }) {
  return (
    <span
      className={`rounded-full border px-1.5 py-0.5 text-[9px] ${
        entry.status === 'pending'
          ? 'border-amber-400/30 bg-amber-400/10 text-amber-200'
          : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200'
      }`}
    >
      {memoryStatusLabel(entry.status)}
    </span>
  );
}

function MemoryRow({
  entry,
  busy,
  disabled,
  onApprove,
  onDelete,
}: {
  entry: MemoryEntry;
  busy: boolean;
  disabled: boolean;
  onApprove(entry: MemoryEntry): void;
  onDelete(entry: MemoryEntry): void;
}) {
  return (
    <li
      data-testid="memory-row"
      data-id={entry.id}
      data-status={entry.status}
      className="rounded-xl border border-white/5 bg-white/[0.03] px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-1.5">
            <KindBadge entry={entry} />
            <StatusBadge entry={entry} />
            {entry.sensitive ? (
              <span className="rounded-full border border-rose-400/30 bg-rose-400/10 px-1.5 py-0.5 text-[9px] text-rose-200">
                敏感
              </span>
            ) : null}
          </p>
          <p className="mt-1 break-words whitespace-pre-wrap text-xs leading-relaxed text-slate-200">
            {entry.text}
          </p>
          <p className="mt-1 text-[10px] text-slate-500">
            更新于 {formatRelative(entry.updated_at, nowSeconds())}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {memoryNeedsApproval(entry) ? (
            <button
              type="button"
              data-testid="memory-approve"
              disabled={disabled}
              onClick={() => onApprove(entry)}
              className="rounded-lg border border-indigo-400/40 bg-indigo-400/15 px-2.5 py-1 text-[11px] text-indigo-100 disabled:opacity-40"
            >
              {busy ? '保存中…' : '确认保存'}
            </button>
          ) : null}
          <button
            type="button"
            data-testid="memory-delete"
            disabled={disabled}
            title="删除这条记忆（不可恢复）"
            onClick={() => onDelete(entry)}
            className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
          >
            删除
          </button>
        </div>
      </div>
    </li>
  );
}

function MemoryGroup({
  title,
  entries,
  busyId,
  loading,
  onApprove,
  onDelete,
}: {
  title: string;
  entries: readonly MemoryEntry[];
  busyId: string | null;
  loading: boolean;
  onApprove(entry: MemoryEntry): void;
  onDelete(entry: MemoryEntry): void;
}) {
  if (entries.length === 0) return null;
  return (
    <div>
      <div className="flex items-center gap-2 pb-1">
        <h3 className="text-[11px] font-medium text-slate-300">{title}</h3>
        <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
          {entries.length}
        </span>
        <span className="h-px flex-1 bg-white/5" />
      </div>
      <ul className="space-y-2">
        {entries.map((entry) => (
          <MemoryRow
            key={entry.id}
            entry={entry}
            busy={busyId === entry.id}
            disabled={loading}
            onApprove={onApprove}
            onDelete={onDelete}
          />
        ))}
      </ul>
    </div>
  );
}

export function MemorySettingsSection() {
  const {
    settings,
    memoryAvailable,
    memoryUnavailableReason,
    memories,
    memoryLoading,
    memoryError,
    refreshMemories,
    deleteMemory,
    approveMemory,
    clearMemories,
  } = useApp();
  const [open, setOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const list = memories ?? [];
  const counts = memoryCounts(list);
  const { active, pending } = partitionMemories(list);

  // Load once as soon as the transport is available; never invent rows.
  useEffect(() => {
    if (memoryAvailable) void refreshMemories();
  }, [memoryAvailable, refreshMemories]);

  const approve = async (entry: MemoryEntry): Promise<void> => {
    if (memoryLoading) return;
    setBusyId(entry.id);
    setActionError(null);
    const result = await approveMemory(entry.id);
    if (!result.ok) setActionError(result.error ?? '确认保存失败');
    setBusyId(null);
  };

  const remove = async (entry: MemoryEntry): Promise<void> => {
    if (memoryLoading) return;
    setBusyId(entry.id);
    setActionError(null);
    const result = await deleteMemory(entry.id);
    if (!result.ok) setActionError(result.error ?? '删除失败');
    setBusyId(null);
  };

  const clearAll = async (): Promise<void> => {
    if (memoryLoading) return;
    setActionError(null);
    const result = await clearMemories();
    if (result.ok) setConfirmClear(false);
    else setActionError(result.error ?? '清除失败');
  };

  const refresh = (): void => {
    setActionError(null);
    void refreshMemories();
  };

  const errorBanner = memoryError ? (
    <div
      data-testid="memory-error"
      className="rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200"
    >
      <p className="break-words">{memoryError}</p>
      {memoryAvailable ? (
        <button
          type="button"
          onClick={refresh}
          disabled={memoryLoading}
          className="mt-2 rounded-lg border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
        >
          重试
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <details
      data-testid="memory-settings"
      open={open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
      className="rounded-2xl border border-white/10 bg-[#0f1629] p-4"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <Icon name="spark" className="h-4 w-4 shrink-0 text-indigo-300" />
          <h2 className="text-sm font-semibold text-slate-100">长期记忆</h2>
          {memories !== null ? (
            <span className="rounded-full border border-white/10 bg-white/5 px-1.5 py-0.5 text-[10px] text-slate-400">
              {counts.total}
            </span>
          ) : null}
          {counts.pending > 0 ? (
            <span className="rounded-full border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 text-[10px] text-amber-200">
              {counts.pending} 待确认
            </span>
          ) : null}
        </span>
        <span className="shrink-0 text-[10px] text-slate-500">{open ? '收起' : '展开'}</span>
      </summary>
      <p className="mt-1 text-[11px] text-slate-500">
        daemon 本机保存的偏好/事实/项目；敏感内容先进入待确认。仅本机，不上云。
      </p>

      <div className="mt-3 space-y-2.5">
        {!memoryAvailable ? (
          <div
            data-testid="memory-unavailable"
            className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] leading-relaxed text-amber-200"
          >
            {memoryUnavailableReason ?? '长期记忆当前不可用'}。此区域不会显示或伪造记忆数据。
          </div>
        ) : memories === null ? (
          <div className="space-y-2">
            {errorBanner}
            <p className="text-[11px] text-slate-500">
              {memoryLoading ? '正在读取长期记忆…' : '尚未加载长期记忆。'}
            </p>
            <button
              type="button"
              data-testid="memory-refresh"
              disabled={memoryLoading}
              onClick={refresh}
              className="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
            >
              {memoryLoading ? '读取中…' : '读取长期记忆'}
            </button>
          </div>
        ) : (
          <>
            {errorBanner}
            {actionError ? (
              <p className="break-words rounded-xl border border-rose-400/25 bg-rose-400/10 px-3 py-2 text-[11px] leading-relaxed text-rose-200">
                {actionError}
              </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-[10px] text-slate-500">
                已启用 {counts.active} · 待确认 {counts.pending}
              </p>
              <span className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="memory-refresh"
                  disabled={memoryLoading}
                  onClick={refresh}
                  className="flex items-center gap-1 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-[11px] text-slate-300 disabled:opacity-40"
                >
                  <Icon
                    name="refresh"
                    className={`h-3 w-3 ${memoryLoading ? 'animate-spin' : ''}`}
                  />
                  刷新
                </button>
                <button
                  type="button"
                  data-testid="memory-clear-all"
                  disabled={memoryLoading || counts.total === 0}
                  onClick={() => {
                    setActionError(null);
                    setConfirmClear(true);
                  }}
                  className="rounded-lg border border-rose-400/30 bg-rose-400/10 px-2.5 py-1 text-[11px] text-rose-100 disabled:opacity-40"
                >
                  全部清除
                </button>
              </span>
            </div>

            {counts.total === 0 ? (
              <p className="rounded-xl border border-white/5 bg-white/[0.02] px-3 py-2 text-[11px] leading-relaxed text-slate-500">
                暂无长期记忆。聊天中出现的偏好/事实/项目会由 daemon
                记录；敏感内容会先出现在这里等你确认。
              </p>
            ) : (
              <div className="space-y-3">
                <MemoryGroup
                  title="待确认"
                  entries={pending}
                  busyId={busyId}
                  loading={memoryLoading}
                  onApprove={(entry) => void approve(entry)}
                  onDelete={(entry) => void remove(entry)}
                />
                <MemoryGroup
                  title="已启用"
                  entries={active}
                  busyId={busyId}
                  loading={memoryLoading}
                  onApprove={(entry) => void approve(entry)}
                  onDelete={(entry) => void remove(entry)}
                />
              </div>
            )}

            {settings.demoMode ? (
              <p className="flex items-start gap-1 text-[10px] leading-relaxed text-sky-200/80">
                <Icon name="spark" className="mt-0.5 h-3 w-3 shrink-0" />
                演示模式：记忆来自本地 mock，不会写入 daemon，也不会上云。
              </p>
            ) : null}

            <ul className="list-disc space-y-1 pl-4 text-[10px] leading-relaxed text-slate-500">
              <li>记忆保存在 PC daemon 本机数据目录；删除立即生效，不影响聊天记录与审批缓存。</li>
              <li>密码/密钥/证件/地址等敏感内容不会自动保存；模型标记敏感时会先进入「待确认」。</li>
              <li>已启用的记忆会以有长度上限的块注入后续聊天，不影响审批与审计红线。</li>
            </ul>
          </>
        )}
      </div>

      <ConfirmDialog
        open={confirmClear}
        title="清除全部长期记忆？"
        danger
        busy={memoryLoading}
        confirmLabel="全部清除"
        onCancel={() => {
          if (memoryLoading) return;
          setConfirmClear(false);
          setActionError(null);
        }}
        onConfirm={() => void clearAll()}
      >
        <p className="text-[11px] leading-relaxed text-slate-400">
          将删除 daemon 本机保存的全部长期记忆（当前 {counts.total}{' '}
          条），无法恢复；不影响聊天记录与审批缓存。
        </p>
        {actionError ? <p className="text-[11px] text-rose-300">{actionError}</p> : null}
      </ConfirmDialog>
    </details>
  );
}
